import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import MoreInfoTooltip from 'components/basic/tooltip/MoreInfoTooltip';
import { filterEmpty } from 'lib/utils';

import SpinningRoundedBorder from '../animations/SpinningRoundedBorder';

import { useAppearAnimationIgnorePeriod } from './appearedDataHook';
import AppearAnimatedRow from './components/AppearAnimatedRow';
import ChangeableAnimatedRow from './components/ChangeableAnimatedRow';

type TableParamsType = {
  $notFixedLayout?: boolean;
  $firstRowPaddingLeft?: string;
  $verticalPadding?: number;
  $stripes?: boolean;
  $loading?: boolean;
  $stackedColumns?: boolean;
  $marginBottom?: string;
  $marginTop?: string;
  $headerBackground: 'black' | 'gray';
};

// https://stackoverflow.com/questions/628301/css3s-border-radius-property-and-border-collapsecollapse-dont-mix-how-can-i
const TableStyled = styled.table<TableParamsType>`
  /** To make "position sticky" on <th/> work */
  position: relative;
  ${({ $marginTop }) => ($marginTop ? `margin-top: ${$marginTop};` : '')}
  ${({ $marginBottom }) =>
    $marginBottom ? `margin-bottom: ${$marginBottom};` : 'margin-bottom: 16px;'}

  text-align: left;
  /** Fix for Sticky header overlay absolutely positioned element (outside of table, causing scroll) */
  /* width: calc(100% - 1px); */
  width: 100%;
  /* border-collapse: collapse; */
  border-collapse: separate;
  border-spacing: 0px;

  & .columnClickable {
    color: var(--secondary);
    cursor: pointer;
    &:hover {
      color: var(--secondaryHover);
      /* opacity: 0.7; */
    }
  }

  ${({ $loading }) => ($loading ? 'opacity: 0.6;' : '')}

  tbody {
    border-radius: 5px;
    border-style: hidden; /* hide standard table (collapsed) border */
    box-shadow: 0 0 0 1px var(--separator); /* this draws the table border  */
  }

  thead {
    /* For sticky border overlay positioning */
    position: relative;
    text-transform: uppercase;
    font-size: var(--smallest);
  }

  /* Sticky header right side border overlay (hide) */
  thead tr:first-child th:last-child::before {
    position: absolute;
    content: '';
    background: ${({ $headerBackground }) =>
      $headerBackground === 'black'
        ? 'var(--black)'
        : 'var(--elevation-light)'};
    width: 2px;
    height: ${({ $stackedColumns }) => ($stackedColumns ? 44 : 29)}px;
    right: -1px;
    top: 0;
  }

  /* Sticky header left side border overlay (hide) */
  thead tr:first-child th:first-child::before {
    position: absolute;
    content: '';
    background: ${({ $headerBackground }) =>
      $headerBackground === 'black'
        ? 'var(--black)'
        : 'var(--elevation-light)'};
    width: 6px;
    height: ${({ $stackedColumns }) => ($stackedColumns ? 44 : 29)}px;
    left: -2px;
    top: 0;
  }

  tbody tr:first-child {
    /* Fix issue with collapsed top border */
    box-shadow: inset 0 1px 1px var(--separator);
    border-radius: 5px;
  }

  & tr:first-child th:first-child {
    /* border: 2px solid orange; */
    border-top-left-radius: 5px;
  }

  & tr:last-child th:last-child {
    /* border: 2px solid green; */
    border-top-right-radius: 5px;
  }

  & tr:last-child td:first-child {
    /* border: 2px solid orange; */
    border-bottom-left-radius: 5px;
  }

  & tr:last-child td:last-child {
    /* border: 2px solid green; */
    border-bottom-right-radius: 5px;
  }

  table-layout: ${({ $notFixedLayout }) =>
    $notFixedLayout ? 'initial' : 'fixed'};

  td:nth-child(1),
  th:nth-child(1) {
    padding-left: ${({ $firstRowPaddingLeft }) =>
      $firstRowPaddingLeft ? $firstRowPaddingLeft : 0};
  }
  ${({ $stripes }) =>
    $stripes &&
    css`
      tbody tr:nth-child(odd) {
        background: transparent;
      }
      tbody tr:nth-child(even) {
        background: var(--black1b);
      }
    `}

  td,
  th {
    border: none;
    padding-top: 7px;
    padding-bottom: 7px;
    padding-right: 10px;
    padding-left: 1px;
    text-align: left;
    white-space: nowrap;
    height: 33px;
  }
  td {
    padding-top: ${({ $verticalPadding }) => $verticalPadding || 6}px;
    padding-bottom: ${({ $verticalPadding }) => $verticalPadding || 6}px;
    color: var(--gray5);
  }

  th {
    color: var(--gray4a);
    position: sticky;
    top: 0;
    z-index: 9;

    height: 23px;

    font-weight: normal;
    background: ${({ $headerBackground }) =>
      $headerBackground === 'black'
        ? 'var(--black)'
        : 'var(--elevation-light)'};
  }
  /* Add sticky header bottom border (needs to respect rounded edges) */
  th::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 1px;
    padding: 0;
    background: var(--separator);
  }
  /* th:first-child::after {
    left: 3px;
    background: var(--separator);
  } */
  th:last-child::after {
    right: 3px;
  }
`;

const SortIconTransparent = styled.span`
  color: transparent;
  opacity: 0;
`;

const SortIcon = styled.span`
  color: var(--primary2);
`;

type IColumn = {
  children: ReactNode;
  left?: boolean;
  right?: boolean;
  center?: boolean;
  textWrap?: boolean;
  allowTruncate?: boolean;
  paddingRight?: number;
  paddingLeft?: number;
  colSpan?: number;
  rowSpan?: number;
  fontFamily?: 'standard' | 'digits';
  /** Use to render absolute positioned content */
  relativePosition?: boolean;
  onClick?: () => void;
};

export function Column({
  children,
  left,
  right,
  center,
  paddingRight,
  paddingLeft,
  textWrap,
  colSpan,
  rowSpan,
  relativePosition,
  allowTruncate,
  fontFamily,
  onClick,
}: IColumn) {
  return (
    <td
      colSpan={colSpan}
      rowSpan={rowSpan}
      style={{
        lineHeight: 1.35,
        fontFamily:
          fontFamily === 'standard'
            ? 'var(--standardFontFamily)'
            : 'var(--digitsFontFamily)',
        textAlign: left
          ? 'left'
          : right
          ? 'right'
          : center
          ? 'center'
          : 'initial',
        ...(paddingRight ? { paddingRight } : {}),
        ...(paddingLeft ? { paddingLeft } : {}),
        ...(onClick ? { cursor: 'pointer' } : {}),
        ...(textWrap ? { whiteSpace: 'initial' } : {}),
        ...(allowTruncate
          ? { textOverflow: 'ellipsis', overflow: 'hidden' }
          : {}),
        ...(relativePosition ? { position: 'relative' } : {}),
      }}
      onClick={onClick}
    >
      {children}
    </td>
  );
}

const SpinnerTd = styled.td`
  ${TableStyled} & {
    text-align: center;
    padding: 18px;
  }
`;

const ErrorTd = styled.td`
  ${TableStyled} & {
    text-align: center;
    color: var(--gray4c);
    padding: 24px;
    font-size: var(--small);
    white-space: normal;
  }
`;

export type RowBackground = 'warn' | 'error';

const getCustomRowBackground = (background?: RowBackground) =>
  !background
    ? undefined
    : background === 'error'
    ? 'var(--red1c)'
    : 'var(--warn)';

function ErrorColumn({
  children,
  colSpan,
}: {
  children: ReactNode;
  colSpan: number;
}) {
  return <ErrorTd colSpan={colSpan}>{children}</ErrorTd>;
}

export type HeaderColumn = {
  name: string;
  nameAsLink?: boolean;
  disabled?: boolean;
  /** Second line name of column (when using stacked values to save space) */
  nameStacked?: string;
  hideName?: boolean;
  align?: 'center' | 'left' | 'right';
  width?: string;
  padding?: string;
  sorted?: number; // -1, 0, 1
  onClick?: () => void;
  tooltip?: string;
  tooltipUrl?: string;
  allowTruncate?: boolean;
};

type ITableProps<T> = {
  loading?: boolean;

  items: T[];
  columns: readonly (undefined | HeaderColumn)[];
  rowCustomizer?: (item: T) => {
    selected?: boolean;
    underlined?: boolean;
    background?: RowBackground;
    fontSize?: string;
  };
  getItemKey: (item: T, index: number) => string;

  rowClickable?: (item: T) => boolean;
  onRowClick?: (item: T) => void;

  renderRow: (item: T, index: number) => ReactNode;
  appearAnimationColor?: (item: T) => 'green' | 'red';
  watchValueAndAnimateOnChange?: (item: T) => string;
  firstColumnPaddingLeft?: string;
  errorText?: ReactNode;
  rowsVerticalPadding?: number;

  /* Note: this will partially ignore fixed widths of columns */
  notFixedLayout?: boolean;
  noHoverEffect?: boolean;
  stripes?: boolean;
  className?: string;
  marginTop?: string;
  marginBottom?: string;
  /** @deprecated */
  headerBackground?: 'black' | 'gray';
};

function BasicTable<T>({
  loading,
  columns,
  items,
  getItemKey,
  renderRow,
  notFixedLayout,
  noHoverEffect,
  rowCustomizer,
  rowClickable,
  onRowClick,
  appearAnimationColor,
  watchValueAndAnimateOnChange,
  firstColumnPaddingLeft,
  errorText,
  rowsVerticalPadding,
  stripes,
  marginBottom,
  marginTop,
  className,
  headerBackground,
}: ITableProps<T>) {
  const appearAnimationIgnorePeriod = useAppearAnimationIgnorePeriod(items);
  const columnsToRender = columns.filter(filterEmpty);
  /** If we have content, Spinner should be spinning over it (In less ideal position) */
  const overlaySpinner = items && items.length > 0;
  return (
    <>
      {loading && overlaySpinner && (
        <div style={{ position: 'relative', zIndex: 456 }}>
          <div style={{ position: 'absolute', top: 80, left: '45%' }}>
            <SpinningRoundedBorder size={80} />
          </div>
        </div>
      )}
      <TableStyled
        $notFixedLayout={notFixedLayout}
        $firstRowPaddingLeft={firstColumnPaddingLeft}
        $verticalPadding={rowsVerticalPadding}
        $stripes={stripes}
        className={className}
        $loading={loading}
        $stackedColumns={columnsToRender.some(column => !!column.nameStacked)}
        $marginBottom={marginBottom}
        $marginTop={marginTop}
        $headerBackground={headerBackground || 'gray'}
      >
        <thead>
          <tr>
            {columnsToRender.map((item, index) => (
              <th
                key={item.name || `unnamed-${index}`}
                className={item.nameAsLink ? 'columnClickable' : undefined}
                style={{
                  lineHeight: 1.35,
                  ...(columnsToRender[index].align
                    ? {
                        textAlign: columnsToRender[index].align,
                      }
                    : {}),
                  ...(columnsToRender[index].width
                    ? {
                        width: columnsToRender[index].width,
                      }
                    : {}),
                  ...(columnsToRender[index].padding
                    ? {
                        padding: columnsToRender[index].padding,
                      }
                    : {}),
                  ...(item.onClick ? { cursor: 'pointer' } : {}),
                  ...(item.disabled
                    ? { color: 'var(--gray3)', cursor: 'not-allowed' }
                    : {}),
                }}
                onClick={item.disabled ? undefined : item.onClick}
              >
                {!item.hideName && (
                  <MoreInfoTooltip
                    tooltip={item.tooltip}
                    href={item.tooltipUrl}
                  >
                    {item.name}
                    {item.nameStacked && (
                      <>
                        <br /> {item.nameStacked}
                      </>
                    )}
                  </MoreInfoTooltip>
                )}

                {item.sorted !== undefined && (
                  <>
                    {' '}
                    {item.sorted === 0 ? (
                      <>
                        {/* Reserve space for sort icon (Prevent table shifts)
                        For right aligned, we better move label, but cannot have empty space there.
                        */}
                        {item.align === 'right' ? null : (
                          <SortIconTransparent>&#x25BC;</SortIconTransparent>
                        )}
                      </>
                    ) : (
                      <SortIcon>
                        {item.sorted > 0 ? <>&#x25BC;</> : <>&#x25b2;</>}
                      </SortIcon>
                    )}
                  </>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && !overlaySpinner ? (
            <tr>
              <SpinnerTd colSpan={columnsToRender.length}>
                <SpinningRoundedBorder inline size={80} />
              </SpinnerTd>
            </tr>
          ) : errorText ? (
            <tr>
              <ErrorColumn colSpan={columnsToRender.length}>
                {errorText}
              </ErrorColumn>
            </tr>
          ) : (
            items.map((item, index) =>
              watchValueAndAnimateOnChange ? (
                <ChangeableAnimatedRow
                  key={getItemKey(item, index)}
                  watchValueForAnimation={watchValueAndAnimateOnChange(item)}
                  onClick={
                    (!rowClickable || rowClickable(item)) && onRowClick
                      ? () => onRowClick(item)
                      : undefined
                  }
                  isSelected={
                    rowCustomizer
                      ? rowCustomizer(item).selected || false
                      : false
                  }
                  isUnderlined={
                    rowCustomizer
                      ? rowCustomizer(item).underlined || false
                      : false
                  }
                  backgroundColor={getCustomRowBackground(
                    rowCustomizer?.(item).background
                  )}
                  fontSize={rowCustomizer?.(item).fontSize}
                >
                  {renderRow(item, index)}
                </ChangeableAnimatedRow>
              ) : (
                <AppearAnimatedRow
                  key={getItemKey(item, index)}
                  $noHoverEffect={noHoverEffect}
                  $stripes={appearAnimationColor ? stripes : undefined}
                  onClick={
                    (!rowClickable || rowClickable(item)) && onRowClick
                      ? () => onRowClick(item)
                      : undefined
                  }
                  className={
                    appearAnimationColor
                      ? `color-${appearAnimationColor(item)}`
                      : ''
                  }
                  $appearAnimationIgnorePeriod={appearAnimationIgnorePeriod}
                  style={
                    rowCustomizer && rowCustomizer(item).selected
                      ? { background: 'var(--primary8)' }
                      : rowCustomizer && rowCustomizer(item).background
                      ? {
                          background: getCustomRowBackground(
                            rowCustomizer(item).background
                          ),
                        }
                      : undefined
                  }
                  fontSize={
                    rowCustomizer ? rowCustomizer(item).fontSize : undefined
                  }
                >
                  {renderRow(item, index)}
                </AppearAnimatedRow>
              )
            )
          )}
        </tbody>
      </TableStyled>
    </>
  );
}

export default BasicTable;
